import {
  CruisePassengerAgeGroup,
  IProfile,
  ITripRoomGuest,
  TripRoomGuestID,
} from "@cruisen/contract"
import { defaultCountries } from "react-international-phone"
import { formatDate, getAgeGroupOptions } from "@cruisen/react-shared"
import { _t, TxKeyPath } from "@cruisen/i18n"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"

const phoneUtil = PhoneNumberUtil.getInstance()

const requiredFields = [
  "title",
  "firstName",
  "lastName",
  "dob",
  "nationality",
  "address",
  "city",
  "postcode",
  "country",
]

export const getUTCDate = (date: string) => {
  if (date.includes("/")) {
    const [day, month, year] = date.split("/").map((text) => Number(text))
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
  }

  const [year, month, day] = date.split("-").map((text) => Number(text))
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
}

export const generateProfileGridData = ({
  profile,
  unmaskedPassportNumber,
  isPassportRevealing,
}: {
  profile: IProfile
  unmaskedPassportNumber: string | null
  isPassportRevealing: boolean
}) => {
  const nationality = profile.nationality
    ? defaultCountries.find((country) => country[1] === profile.nationality?.toLowerCase())?.[0] ||
      profile.nationality
    : ""
  const country = profile.country
    ? defaultCountries.find((country) => country[1] === profile.country?.toLowerCase())?.[0] ||
      profile.country
    : ""
  const county =
    profile.country === "US" && profile.county
      ? _t(`profileScreen.statePicker.states.${profile.county.toLowerCase()}` as TxKeyPath)?.[
          "name"
        ] || profile.county
      : ""
  const passportIssuingCountry = profile.passportIssuingCountry
    ? defaultCountries.find(
        (country) => country[1] === profile.passportIssuingCountry?.toLowerCase(),
      )?.[0] || profile.passportIssuingCountry
    : ""

  let phoneNumber: string
  try {
    phoneNumber = profile.phone
      ? phoneUtil.format(phoneUtil.parseAndKeepRawInput(profile.phone), PhoneNumberFormat.NATIONAL)
      : ""
  } catch (err) {
    phoneNumber = profile.phone
    console.error("Error formatting phone number", err)
  }

  const ageGroup = getAgeGroupOptions().find((item) => item.value === profile.ageGroup)

  let dob: string
  try {
    dob = profile.dob ? formatDate(getUTCDate(profile.dob).toISOString(), "MM/dd/yyyy") : ""
  } catch (err) {
    dob = profile.dob
    console.error("Error formatting date of birth", err)
  }

  let passportExpireDate: string
  try {
    passportExpireDate = profile.passportExpireDate
      ? formatDate(getUTCDate(profile.passportExpireDate).toISOString(), "MM/dd/yyyy")
      : ""
  } catch (err) {
    passportExpireDate = profile.passportExpireDate
    console.error("Error formatting passport expire date", err)
  }

  let passportIssueDate: string
  try {
    passportIssueDate = profile.passportIssueDate
      ? formatDate(getUTCDate(profile.passportIssueDate).toISOString(), "MM/dd/yyyy")
      : ""
  } catch (err) {
    passportIssueDate = profile.passportIssueDate
    console.error("Error formatting passport issue date", err)
  }

  return {
    personalInfo: [
      {
        label: "Title",
        value: profile.title
          ? `${profile.title.charAt(0).toUpperCase()}${profile.title.slice(1)}`
          : "",
        formName: "title",
        formValue: profile.title || "",
      },
      {
        label: "First",
        value: profile.firstName,
        formName: "firstName",
      },
      {
        label: "Last",
        value: profile.lastName,
        formName: "lastName",
      },
      {
        label: "Age group",
        value: ageGroup ? `${ageGroup.label} ${ageGroup.range}` : "",
        formName: "ageGroup",
        formValue: profile.ageGroup || "",
      },
      {
        label: "Date of birth",
        value: dob,
        formName: "dob",
      },
      {
        label: "Nationality",
        value: nationality || "",
        formName: "nationality",
        formValue: profile.nationality || "",
      },
      {
        label: "Phone",
        value: phoneNumber,
        optional: true,
        formName: "phone",
        formValue: profile.phone || "",
      },
      ...(profile.email
        ? [
            {
              label: "Email",
              value: profile.email,
              formName: "email",
            },
          ]
        : []),
    ],
    passportDetails: [
      {
        label: "Passport number",
        value: isPassportRevealing
          ? "%skeleton%"
          : unmaskedPassportNumber || profile.passportNumber || "",
        optional: true,
        formName: "passportNumber",
      },
      {
        label: "Issuing country",
        value: passportIssuingCountry || "",
        optional: !profile.passportNumber,
        formName: "passportIssuingCountry",
        formValue: profile.passportIssuingCountry || "",
      },
      {
        label: "Issue date",
        value: passportIssueDate,
        optional: !profile.passportNumber,
        formName: "passportIssueDate",
      },
      {
        label: "Expire date",
        value: passportExpireDate,
        optional: !profile.passportNumber,
        formName: "passportExpireDate",
      },
    ],
    residence: [
      {
        label: "Address",
        value: profile.address || "",
        formName: "address",
      },
      {
        label: "City",
        value: profile.city || "",
        formName: "city",
      },
      ...(profile.country === "US" || !profile.country
        ? [
            {
              label: "State",
              value: county,
              formName: "county",
              formValue: profile.county || "",
            },
          ]
        : []),
      {
        label: "Zip code",
        value: profile.postcode || "",
        formName: "postcode",
      },
      {
        label: "Country",
        value: country,
        formName: "country",
        formValue: profile.country || "",
      },
    ],
    cruiseLineLoyaltyMembers:
      profile?.cruiseLineLoyaltyMembers?.map((member) => ({
        label: member.line.name,
        value: member.loyaltyCode,
        formName: `cruiseLine+${member.line.id}`,
        formValue: member.loyaltyCode,
      })) || [],
  }
}

export const isPassengerDataFilled = (profile: IProfile) => {
  const allFieldsFilled = requiredFields.every((field) => profile[field as keyof IProfile])
  const isUSCountyFilled = profile.country !== "US" || profile.county

  return Boolean(allFieldsFilled && isUSCountyFilled)
}

export type BookingModalProfile = IProfile & { isDataFilled: boolean; guestId: TripRoomGuestID }

export const generateSkeletonProfile = (): BookingModalProfile => ({
  guestId: "",
  id: -1,
  isDataFilled: true,
  address: "%skeleton%",
  ageGroup: "%skeleton%" as CruisePassengerAgeGroup,
  city: "%skeleton%",
  country: "%skeleton%",
  county: "%skeleton%",
  dob: "%skeleton%",
  email: "%skeleton%",
  firstName: "%skeleton%",
  gender: "%skeleton%",
  isPrimaryProfile: false,
  lastName: "%skeleton%",
  nationality: "%skeleton%",
  ownerUserId: "",
  phone: "%skeleton%",
  postcode: "%skeleton%",
  title: "%skeleton%",
  created: undefined,
  updated: undefined,
})

export const generateUnclaimedProfile = (guest: ITripRoomGuest): BookingModalProfile => ({
  guestId: guest.id,
  id: -1,
  isDataFilled: false,
  address: "",
  ageGroup: guest.ageGroup as CruisePassengerAgeGroup,
  city: "",
  country: "",
  county: "",
  dob: "",
  email: "",
  firstName: guest.firstName,
  gender: "",
  isPrimaryProfile: Boolean(guest.userId),
  lastName: guest.lastName,
  nationality: "",
  ownerUserId: guest.userId || "",
  phone: guest.phoneNumber,
  postcode: "",
  title: "",
  created: undefined,
  updated: undefined,
})

export const prepareTripRoomGuestProfiles = (
  guests: ITripRoomGuest[],
  profiles?: IProfile[],
  isLoadingProfiles?: boolean,
): BookingModalProfile[] => {
  return guests.reduce((acc, guest) => {
    if (isLoadingProfiles) {
      acc.push({
        ...generateSkeletonProfile(),
        guestId: guest.id,
        id: guest.profileId,
        firstName: guest.firstName,
        lastName: guest.lastName,
        email: guest.email,
        phone: guest.phoneNumber,
        ageGroup: guest.ageGroup,
        dob: guest.dob,
      })
    } else {
      const profile = profiles?.find((item) => item.id === guest.profileId)
      acc.push(prepareTripRoomGuestProfile(guest, profile))
    }
    return acc
  }, [] as BookingModalProfile[])
}

export const prepareTripRoomGuestProfile = (guest: ITripRoomGuest, profile?: IProfile) => {
  if (profile) {
    return {
      ...profile,
      isDataFilled: isPassengerDataFilled(profile),
      guestId: guest.id,
    }
  } else {
    return generateUnclaimedProfile(guest)
  }
}

export const currencyToNumber = (value: string) => {
  if (!value) return 0
  return Number(value.replace(/,/g, ""))
}
